import React, { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import WorkExperienceForm from "../components/WorkExperienceForm";
import { useResume } from "../hooks/useResume";
import { useAuth } from "../contexts/useAuth";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../contexts/TranslationContext";
import html2pdf from 'html2pdf.js';
import ResumePdfContent from "../components/ResumePdfContent";
import { Modal } from "../components/Modal";
import { emailValidation, phoneValidation, urlValidation, requiredValidation } from "../utils/validation.js";
import { Loader2, Pencil, FileText, Trash2, Sparkles } from "lucide-react";


export default function ConfigureResume() {
  const { token } = useAuth();

  const navigate = useNavigate();
  const {
    resume,
    saveResume,
    isEditing,
    setIsEditing,
    loading
  } = useResume(token);

  const [resumesAlternativos, setResumesAlternativos] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState(null);
  const { t } = useTranslation();
  const [cvSelecionado, setCvSelecionado] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [altIndex, setAltIndex] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const fetchAlternativos = async () => {
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/resumes/alternativos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const ordenados = [...data].sort((a, b) =>
        (a.nomeCurriculo || "").localeCompare(b.nomeCurriculo || "", undefined, {
          sensitivity: "base",
          numeric: true,
        })
      );
      setResumesAlternativos(ordenados);
    } catch {
      toast.error(t('toasts.resumeFetchError'));
    }
  };

  const confirmDelete = async (id) => {
    setDeleteModal({ open: false, id: null });
    try {
      await fetch(`${import.meta.env.VITE_API_URL}/resumes/alternativos/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(t('toasts.resumeDeleted'));
      setAltIndex(0);
      await fetchAlternativos();
    } catch {
      toast.error(t('toasts.resumeDeleteError'));
    }
  };

  const handleEdit = (cv) => {
    setEditingId(cv._id);
    setEditingTitle(cv.nomeCurriculo);
    setIsEditing(true);

    const normalized = {
      ...cv,
      experiences: cv.experiences.map(exp => ({
        company: exp.empresa,
        role: exp.cargo,
        descricao: exp.descricao,
        startDate: exp.startDate ? formatDate(exp.startDate) : "",
        endDate: exp.endDate ? formatDate(exp.endDate) : "",
        isCurrentJob: exp.isCurrentJob || false
      })),
      skills: cv.skills || [],
      languages: cv.languages || [],
      references: Array.isArray(cv.references)
        ? cv.references.map(r => {
          const name = r.name || "";
          const contact = r.contact || "";
          return contact ? `${name} - ${contact}` : name;
        }).join("\n")
        : "",
    };

    methods.reset(normalized);
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  useEffect(() => {
    fetchAlternativos();
  }, []);

  const defaultResume = {
    name: "",
    phone: "",
    email: "",
    address: "",
    website: "",
    linkedin: "",
    github: "",
    desiredRole: "",
    summary: "",
    skills: [],
    languages: [],
    references: [],
    experiences: []
  };

  const initialResume = {
    ...resume,
    references: Array.isArray(resume?.references)
      ? resume.references.map(r => {
        const name = r.name || "";
        const contact = r.contact || "";
        return contact ? `${name} - ${contact}` : name;
      }).join("\n")
      : ""
  };

  const methods = useForm({
    defaultValues: initialResume || defaultResume,
    values: initialResume || defaultResume,
    mode: "onBlur"
  });

  const { register, handleSubmit, formState: { errors } } = methods;

  const onSubmit = async (data) => {
    setIsSaving(true);
    try {
      if (editingId) {
        // Currículo alternativo
        const payload = {
        nomeCurriculo: editingTitle,
        name: data.name,
        phone: data.phone,
        email: data.email,
        address: data.address,
        website: data.website,
        linkedin: data.linkedin,
        github: data.github,
        desiredRole: data.desiredRole,
        summary: data.summary,
        skills: typeof data.skills === "string" ? data.skills.split(",").map(s => s.trim()) : data.skills,
        languages: typeof data.languages === "string" ? data.languages.split(",").map(l => l.trim()) : data.languages,
        references: typeof data.references === "string"
          ? data.references.split("\n").map(ref => {
            const [name, contact] = ref.split("-").map(str => str.trim());
            return { name: name || "", contact: contact || "" };
          })
          : data.references,
        experiences: data.experiences.map(exp => ({
          empresa: exp.company || exp.empresa || "",
          cargo: exp.role || exp.cargo || "",
          descricao: exp.descricao || "",
          startDate: exp.startDate || null,
          endDate: exp.endDate || null,
          isCurrentJob: exp.isCurrentJob || false
        })),
          education: data.education || []
        };

        const response = await fetch(`${import.meta.env.VITE_API_URL}/resumes/alternativos/${editingId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) throw new Error();

        toast.success(t('toasts.altResumeUpdated'));
        setEditingId(null);
        setEditingTitle(null);
        setIsEditing(false);
        fetchAlternativos();
      } else {
      // Currículo original
      const payload = {
        name: data.name,
        phone: data.phone,
        email: data.email,
        address: data.address,
        website: data.website,
        linkedin: data.linkedin,
        github: data.github,
        desiredRole: data.desiredRole,
        summary: data.summary,
        skills: typeof data.skills === "string" ? data.skills.split(",").map(s => s.trim()) : data.skills,
        languages: typeof data.languages === "string" ? data.languages.split(",").map(l => l.trim()) : data.languages,
        references: typeof data.references === "string"
          ? data.references.split("\n").map(ref => {
            const [name, contact] = ref.split("-").map(str => str.trim());
            return {
              name: name || "",
              contact: contact || ""
            };
          })
          : data.references,
        education: data.education || [],
        experiences: data.experiences.map(exp => ({
          company: exp.company || "",
          role: exp.role || "",
          descricao: exp.descricao || "",
          startDate: exp.startDate || null,
          endDate: exp.endDate || null,
          isCurrentJob: exp.isCurrentJob || false
        }))
      };

        const result = await saveResume(payload);
        if (result.success) {
          toast.success(t('toasts.resumeSaved'));
          setIsEditing(false);
        } else {
          toast.error(t('toasts.resumeSaveError'));
        }
      }
    } catch (error) {
      toast.error(t('toasts.altResumeUpdateError'));
    } finally {
      setIsSaving(false);
    }
  };


  const formatDescricao = (descricao) =>
    descricao
      .split(/(?<=\.\s)/)
      .filter(Boolean)
      .map((sent, idx) => (
        <p key={idx} className="text-sm text-gray-400 ml-4 whitespace-pre-line">{sent.trim()}</p>
      ));

  function formatDate(date) {
    if (!date) return "";
    const newDate = new Date(date);
    return newDate.toISOString().split('T')[0];
  }

  function formatExperiencePeriod(exp, t) {
    if (!exp.startDate) return "";
    const start = new Date(exp.startDate);
    const startMonth = start.toLocaleString('default', { month: 'short' });
    const startYear = start.getFullYear();

    if (exp.isCurrentJob) {
      return `${startMonth} ${startYear} - ${t('configureResume.experienceForm.present') || 'Present'}`;
    }

    if (exp.endDate) {
      const end = new Date(exp.endDate);
      const endMonth = end.toLocaleString('default', { month: 'short' });
      const endYear = end.getFullYear();
      return `${startMonth} ${startYear} - ${endMonth} ${endYear}`;
    }

    return `${startMonth} ${startYear}`;
  }

  useEffect(() => {
    if (resume) {
      methods.reset({
        ...resume,
        education: resume.education?.map((edu) => ({
          ...edu,
          startDate: edu.startDate ? formatDate(edu.startDate) : "",
          endDate: edu.endDate ? formatDate(edu.endDate) : "",
        })) || [],
        references: Array.isArray(resume.references)
          ? resume.references.map(r => `${r.name} - ${r.contact}`).join("\n")
          : "",
      });
    }
  }, [resume, methods]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 py-10 text-white">
        <div className="w-full max-w-5xl animate-pulse space-y-6">
          <div className="h-10 w-64 bg-white/10 rounded mx-auto" />

          <div className="bg-[#111] border border-white/10 rounded-xl p-6 shadow-lg space-y-4">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="space-y-1">
                <div className="h-4 w-24 bg-white/10 rounded" />
                <div className="h-4 w-full bg-white/5 rounded" />
              </div>
            ))}

            <div className="pt-4 space-y-3">
              <div className="h-5 w-40 bg-white/10 rounded" />
              {[...Array(2)].map((_, i) => (
                <div key={i} className="space-y-1 ml-4">
                  <div className="h-4 w-48 bg-white/10 rounded" />
                  <div className="h-3 w-full bg-white/5 rounded" />
                  <div className="h-3 w-3/4 bg-white/5 rounded" />
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-4">
            <div className="h-12 w-full bg-white/10 rounded-full" />
            <div className="h-12 w-full bg-white/10 rounded-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-[#0a0a0a] text-white px-6 pt-12 pb-6">
      {/* Comparativo lado a lado precisa de mais largura que o resto da pagina */}
      <div className={`mx-auto w-full ${resumesAlternativos.length > 0 ? 'max-w-[1400px]' : 'max-w-5xl'}`}>
        <h1 className="text-2xl font-bold text-white mb-6">
          {t('configureResume.resumeConfig')}
        </h1>

      {!isEditing && resume && (
        <div className="bg-[#111] border border-white/10 rounded-lg p-6 mb-8">
          {/*
            Subgrid: as duas colunas compartilham as linhas (header / abas / documento),
            entao o documento branco comeca na mesma altura mesmo com a linha de abas
            existindo so na coluna da direita.
          */}
          <div className={`grid gap-y-8 ${resumesAlternativos.length > 0 ? 'lg:grid-cols-2 lg:grid-rows-[auto_auto_1fr] lg:gap-y-0 lg:gap-x-8' : 'grid-cols-1'}`}>

            {/* Original CV */}
            <div className={`w-full min-w-0 ${resumesAlternativos.length > 0 ? 'lg:row-span-3 lg:grid lg:grid-rows-subgrid' : ''}`}>
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-brand-500">{t('configureResume.labelOriginal')}</span>
                </div>
                <div className="flex gap-2 items-center">
                  <button
                    onClick={() => navigate("/generate-resume-ai")}
                    className="flex items-center gap-1.5 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 hover:text-indigo-300 px-3 py-1.5 rounded-md text-xs font-medium transition border border-indigo-500/20"
                  >
                    <Sparkles size={14} />
                    {t('configureResume.aiGeneratorResume')}
                  </button>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 hover:text-blue-300 px-3 py-1.5 rounded-md text-xs font-medium transition border border-blue-500/20"
                  >
                    <Pencil size={14} />
                    {t('configureResume.edit')}
                  </button>
                  <button
                    onClick={() => setCvSelecionado({
                      ...resume,
                      nomeCurriculo: "Original",
                      useAppLanguage: true,
                      experiences: (resume.experiences || []).map(exp => ({
                        empresa: exp.company || "",
                        cargo: exp.role || "",
                        descricao: exp.descricao || ""
                      }))
                    })}
                    className="flex items-center gap-1.5 bg-green-500/10 hover:bg-green-500/20 text-green-400 hover:text-green-300 px-3 py-1.5 rounded-md text-xs font-medium transition border border-green-500/20"
                  >
                    <FileText size={14} />
                    {t('configureResume.visualizePdf')}
                  </button>
                </div>
              </div>
              {/* Espelha a linha de abas da coluna da direita, para os CVs alinharem */}
              {resumesAlternativos.length > 0 && <div className="hidden lg:block" aria-hidden="true" />}

              <div className="bg-white shadow-2xl border border-gray-200" style={{ fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif" }}>
                <div className="px-8 py-6 text-[13px] text-gray-900">
                  <h2 className="text-xl font-bold mb-1">{resume.name || "—"}</h2>
                  {resume.desiredRole && <p className="text-sm text-gray-600 mb-2">{resume.desiredRole}</p>}
                  {resume.summary && <p className="text-xs text-gray-700 mb-4 leading-relaxed">{resume.summary}</p>}

                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-gray-600 mb-4">
                    {resume.phone && <p>{resume.phone}</p>}
                    {resume.email && <p>{resume.email}</p>}
                    {resume.address && <p>{resume.address}</p>}
                    {resume.linkedin && <p>{resume.linkedin}</p>}
                    {resume.github && <p>{resume.github}</p>}
                    {resume.website && <p>{resume.website}</p>}
                  </div>

                  {resume.skills?.length > 0 && (
                    <>
                      <h3 className="text-xs font-semibold text-gray-800 uppercase tracking-wide mb-1">{t('configureResume.skills')}</h3>
                      <p className="text-xs text-gray-700 mb-4">{resume.skills.join(", ")}</p>
                    </>
                  )}

                  <h3 className="text-xs font-semibold text-gray-800 uppercase tracking-wide mb-1">{t('configureResume.experiences')}</h3>
                  {resume.experiences?.length > 0 ? (
                    resume.experiences.map((exp, idx) => (
                      <div key={idx} className="mb-3">
                        <p className="font-semibold text-xs">{exp.role} @ {exp.company}</p>
                        {(exp.startDate || exp.endDate || exp.isCurrentJob) && (
                          <p className="text-[10px] text-gray-500 italic">{formatExperiencePeriod(exp, t)}</p>
                        )}
                        {exp.descricao && <p className="text-[11px] text-gray-600 whitespace-pre-line mt-0.5">{exp.descricao}</p>}
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-gray-400 italic mb-3">{t('configureResume.noExperience')}</p>
                  )}

                  {resume.education?.length > 0 && (
                    <>
                      <h3 className="text-xs font-semibold text-gray-800 uppercase tracking-wide mb-1">{t('configureResume.education')}</h3>
                      {resume.education.map((edu, idx) => (
                        <div key={idx} className="mb-2">
                          <p className="font-semibold text-xs">{edu.course} @ {edu.university}</p>
                          <p className="text-[11px] text-gray-600">
                            {edu.startDate ? new Date(edu.startDate).getFullYear() : ""}
                            {edu.startDate && edu.endDate ? " - " : ""}
                            {edu.endDate ? new Date(edu.endDate).getFullYear() : ""}
                          </p>
                        </div>
                      ))}
                    </>
                  )}

                  {resume.languages?.length > 0 && (
                    <>
                      <h3 className="text-xs font-semibold text-gray-800 uppercase tracking-wide mb-1">{t('configureResume.languages')}</h3>
                      <p className="text-xs text-gray-700 mb-4">{resume.languages.join(" | ")}</p>
                    </>
                  )}

                  {resume.references?.length > 0 && (
                    <>
                      <h3 className="text-xs font-semibold text-gray-800 uppercase tracking-wide mb-1">{t('configureResume.references')}</h3>
                      <ul className="list-disc ml-4 text-xs text-gray-700">
                        {resume.references.map((ref, idx) => (
                          <li key={idx}>{ref.name}{ref.contact ? `, ${ref.contact}` : ""}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Alternative CV (side by side) */}
            {resumesAlternativos.length > 0 && (() => {
              const cv = resumesAlternativos[altIndex];
              return (
                <div className="w-full min-w-0 lg:row-span-3 lg:grid lg:grid-rows-subgrid">
                  {/* Nome do CV fica nas abas abaixo, por isso nao se repete aqui */}
                  <div className="flex justify-between items-center gap-3 mb-2">
                    <span className="shrink-0 inline-block border border-indigo-500 text-indigo-400 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded leading-none">{t('configureResume.labelAiGenerated')}</span>
                    <div className="flex gap-2 items-center shrink-0">
                      <button
                        onClick={() => handleEdit(cv)}
                        className="flex items-center gap-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 hover:text-blue-300 px-3 py-1.5 rounded-md text-xs font-medium transition border border-blue-500/20"
                      >
                        <Pencil size={14} />
                        {t('configureResume.edit')}
                      </button>
                      <button
                        onClick={() => setCvSelecionado(cv)}
                        className="flex items-center gap-1.5 bg-green-500/10 hover:bg-green-500/20 text-green-400 hover:text-green-300 px-3 py-1.5 rounded-md text-xs font-medium transition border border-green-500/20"
                      >
                        <FileText size={14} />
                        {t('configureResume.visualizePdf')}
                      </button>
                      <button
                        onClick={() => setDeleteModal({ open: true, id: cv._id })}
                        className="flex items-center gap-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 px-3 py-1.5 rounded-md text-xs font-medium transition border border-red-500/20"
                      >
                        <Trash2 size={14} />
                        {t('configureResume.delete')}
                      </button>
                    </div>
                  </div>

                  {/* Tabs: one per AI-generated resume */}
                  <div className="scrollbar-slim flex gap-2 mb-2 overflow-x-auto pb-2">
                    {resumesAlternativos.map((r, idx) => (
                      <button
                        key={r._id || idx}
                        onClick={() => setAltIndex(idx)}
                        aria-current={idx === altIndex}
                        title={r.nomeCurriculo}
                        className={`shrink-0 max-w-[220px] truncate px-3 py-1.5 rounded-t-md text-xs font-semibold border-b-2 transition ${
                          idx === altIndex
                            ? "border-brand-500 text-brand-500 bg-white/5"
                            : "border-transparent text-gray-400 hover:text-gray-200 hover:bg-white/5"
                        }`}
                      >
                        {r.nomeCurriculo}
                      </button>
                    ))}
                  </div>

                  <div className="bg-white shadow-2xl border border-gray-200" style={{ fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif" }}>
                    <div className="px-8 py-6 text-[13px] text-gray-900">
                      <h2 className="text-xl font-bold mb-1">{cv.name}</h2>
                      {cv.desiredRole && <p className="text-sm text-gray-600 mb-2">{cv.desiredRole}</p>}
                      {cv.summary && <p className="text-xs text-gray-700 mb-4 leading-relaxed">{cv.summary}</p>}

                      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-gray-600 mb-4">
                        {cv.phone && <p>{cv.phone}</p>}
                        {cv.email && <p>{cv.email}</p>}
                        {cv.address && <p>{cv.address}</p>}
                        {cv.linkedin && <p>{cv.linkedin}</p>}
                        {cv.github && <p>{cv.github}</p>}
                        {cv.website && <p>{cv.website}</p>}
                      </div>

                      {Array.isArray(cv.skills) && cv.skills.length > 0 && (
                        <>
                          <h3 className="text-xs font-semibold text-gray-800 uppercase tracking-wide mb-1">{t('configureResume.skills')}</h3>
                          <p className="text-xs text-gray-700 mb-4">{cv.skills.join(", ")}</p>
                        </>
                      )}

                      {cv.experiences.length > 0 && (
                        <>
                          <h3 className="text-xs font-semibold text-gray-800 uppercase tracking-wide mb-1">{t('configureResume.experiences')}</h3>
                          {cv.experiences.map((exp, idx) => (
                            <div key={idx} className="mb-3">
                              <p className="font-semibold text-xs">{exp.cargo} @ {exp.empresa}</p>
                              {(exp.startDate || exp.endDate || exp.isCurrentJob) && (
                                <p className="text-[10px] text-gray-500 italic">{formatExperiencePeriod(exp, t)}</p>
                              )}
                              {exp.descricao && <p className="text-[11px] text-gray-600 whitespace-pre-line mt-0.5">{exp.descricao}</p>}
                            </div>
                          ))}
                        </>
                      )}

                      {Array.isArray(cv.education) && cv.education.length > 0 && (
                        <>
                          <h3 className="text-xs font-semibold text-gray-800 uppercase tracking-wide mb-1">{t("configureResume.education")}</h3>
                          {cv.education.map((edu, idx) => (
                            <div key={idx} className="mb-2">
                              <p className="font-semibold text-xs">{edu.course} @ {edu.university}</p>
                              <p className="text-[11px] text-gray-600">
                                {edu.startDate ? new Date(edu.startDate).getFullYear() : ""}
                                {edu.startDate && edu.endDate ? " - " : ""}
                                {edu.endDate ? new Date(edu.endDate).getFullYear() : ""}
                              </p>
                            </div>
                          ))}
                        </>
                      )}

                      {Array.isArray(cv.languages) && cv.languages.length > 0 && (
                        <>
                          <h3 className="text-xs font-semibold text-gray-800 uppercase tracking-wide mb-1">{t('configureResume.languages')}</h3>
                          <p className="text-xs text-gray-700 mb-4">{cv.languages.join(" | ")}</p>
                        </>
                      )}

                      {Array.isArray(cv.references) && cv.references.length > 0 && (
                        <>
                          <h3 className="text-xs font-semibold text-gray-800 uppercase tracking-wide mb-1">{t('configureResume.references')}</h3>
                          <ul className="list-disc ml-4 text-xs text-gray-700">
                            {cv.references.map((ref, idx) => (
                              <li key={idx}>{ref.name}{ref.contact ? `, ${ref.contact}` : ""}</li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  </div>

                </div>
              );
            })()}

          </div>
        </div>
      )}

      {(isEditing || !resume) && (
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="w-full bg-[#111] rounded-xl shadow-2xl border border-white/10 overflow-hidden"
            noValidate
          >
            {editingId && (
              <div className="bg-brand-500 text-white text-center font-bold text-sm py-2">
                {t('configureResume.editing')} {editingTitle}
              </div>
            )}

            {/* Section: Personal Info */}
            <div className="px-8 py-6">
              <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 border-b border-white/10 pb-1 mb-4">
                {t('configureResume.personalInfo')}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {["name", "email", "phone", "address"].map((field) => {
                  const getValidation = () => {
                    if (field === "name") return requiredValidation(t, t(`configureResume.${field}`));
                    if (field === "email") return emailValidation(t);
                    if (field === "phone") return phoneValidation(t, true);
                    return {};
                  };

                  return (
                    <div key={field}>
                      <label className="block text-xs font-medium text-gray-400 mb-1">{t(`configureResume.${field}`)}</label>
                      <input
                        type={field === "email" ? "email" : "text"}
                        className={`w-full p-2 rounded bg-white/10 text-white placeholder-gray-500 border focus:border-brand-400 focus:outline-none transition ${
                          errors[field] ? 'border-red-500' : 'border-gray-600'
                        }`}
                        placeholder={t(`configureResume.placeholder.${field}`)}
                        {...register(field, getValidation())}
                      />
                      {errors[field] && (
                        <p className="text-red-500 text-xs mt-1">{errors[field].message}</p>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                {["website", "linkedin", "github"].map((field) => (
                  <div key={field}>
                    <label className="block text-xs font-medium text-gray-400 mb-1">{t(`configureResume.${field}`)}</label>
                    <input
                      type="url"
                      className={`w-full p-2 rounded bg-white/10 text-white placeholder-gray-500 border focus:border-brand-400 focus:outline-none transition ${
                        errors[field] ? 'border-red-500' : 'border-gray-600'
                      }`}
                      placeholder={t(`configureResume.placeholder.${field}`)}
                      {...register(field, urlValidation(t, false))}
                    />
                    {errors[field] && (
                      <p className="text-red-500 text-xs mt-1">{errors[field].message}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Divisor */}
            <div className="border-t border-white/10"></div>

            <div className="px-8 py-6 space-y-6">
              {/* Section: Professional Profile */}
              <div>
                <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 border-b border-white/10 pb-1 mb-4">
                  {t("configureResume.summary")}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">{t("configureResume.desiredRole")}</label>
                    <input
                      type="text"
                      className="w-full p-2 rounded border border-gray-600 bg-white/10 text-white placeholder-gray-500 focus:border-brand-400 focus:outline-none transition"
                      placeholder={t("configureResume.placeholder.desiredRole")}
                      {...register("desiredRole")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">{t("configureResume.summary")}</label>
                    <textarea
                      className="w-full p-2 rounded border border-gray-600 bg-white/10 text-white placeholder-gray-500 focus:border-brand-400 focus:outline-none transition"
                      placeholder={t("configureResume.placeholder.summary")}
                      rows={4}
                      {...register("summary")}
                    />
                  </div>
                </div>
              </div>

              {/* Divisor */}
              <div className="border-t border-white/10"></div>

              {/* Section: Skills & Languages */}
              <div>
                <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 border-b border-white/10 pb-1 mb-4">
                  {t("configureResume.skills")} & {t("configureResume.languages")}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">{t("configureResume.skills")}</label>
                    <input
                      type="text"
                      className="w-full p-2 rounded border border-gray-600 bg-white/10 text-white placeholder-gray-500 focus:border-brand-400 focus:outline-none transition"
                      placeholder={t("configureResume.placeholder.skills")}
                      {...register("skills")}
                    />
                    <p className="text-xs text-gray-400 mt-1">{t("configureResume.hintCommaSeparated")}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">{t("configureResume.languages")}</label>
                    <input
                      type="text"
                      className="w-full p-2 rounded border border-gray-600 bg-white/10 text-white placeholder-gray-500 focus:border-brand-400 focus:outline-none transition"
                      placeholder={t("configureResume.placeholder.languages")}
                      {...register("languages")}
                    />
                    <p className="text-xs text-gray-400 mt-1">{t("configureResume.hintCommaSeparated")}</p>
                  </div>
                </div>
              </div>

              {/* Divisor */}
              <div className="border-t border-white/10"></div>

              {/* Section: Education */}
              <div>
                <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 border-b border-white/10 pb-1 mb-4">
                  {t("configureResume.education")}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">{t("configureResume.university")}</label>
                    <input
                      type="text"
                      className="w-full p-2 rounded border border-gray-600 bg-white/10 text-white placeholder-gray-500 focus:border-brand-400 focus:outline-none transition"
                      placeholder={t("configureResume.placeholder.name")}
                      {...register("education.0.university")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">{t("configureResume.course")}</label>
                    <input
                      type="text"
                      className="w-full p-2 rounded border border-gray-600 bg-white/10 text-white placeholder-gray-500 focus:border-brand-400 focus:outline-none transition"
                      placeholder={t("configureResume.course")}
                      {...register("education.0.course")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">{t("configureResume.startDate")}</label>
                    <input
                      type="date"
                      className="w-full p-2 rounded border border-gray-600 bg-white/10 text-white focus:border-brand-400 focus:outline-none transition"
                      {...register("education.0.startDate")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">{t("configureResume.endDate")}</label>
                    <input
                      type="date"
                      className="w-full p-2 rounded border border-gray-600 bg-white/10 text-white focus:border-brand-400 focus:outline-none transition"
                      {...register("education.0.endDate")}
                    />
                  </div>
                </div>
              </div>

              {/* Divisor */}
              <div className="border-t border-white/10"></div>

              {/* Section: Experience */}
              <div>
                <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 border-b border-white/10 pb-1 mb-4">
                  {t("configureResume.experiences")}
                </h3>
                <WorkExperienceForm />
              </div>

              {/* Divisor */}
              <div className="border-t border-white/10"></div>

              {/* Section: References */}
              <div>
                <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 border-b border-white/10 pb-1 mb-4">
                  {t("configureResume.references")}
                </h3>
                <textarea
                  className="w-full p-2 rounded border border-gray-600 bg-white/10 text-white placeholder-gray-500 focus:border-brand-400 focus:outline-none transition"
                  placeholder={t("configureResume.placeholder.references")}
                  rows={3}
                  {...register("references")}
                />
                <p className="text-xs text-gray-400 mt-1">{t("configureResume.hintReferencesFormat")}</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="px-8 py-4 bg-white/5 border-t border-white/10 flex justify-between gap-4">
              <button
                type="submit"
                disabled={isSaving}
                className="px-6 py-3 rounded-full bg-gradient-to-r from-brand-400 to-brand-600 text-white font-bold hover:scale-105 transition w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSaving && <Loader2 className="animate-spin" size={20} />}
                {t('configureResume.save')}
              </button>

              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setEditingId(null);
                  setEditingTitle(null);
                  methods.reset({
                    ...resume,
                    education: resume?.education || [],
                    references: Array.isArray(resume.references)
                      ? resume.references.map(r => `${r.name} - ${r.contact}`).join("\n")
                      : ""
                  });
                }}
                className="px-6 py-3 rounded-full bg-gray-500 text-white font-bold hover:scale-105 transition w-full"
              >
                {t('configureResume.cancel')}
              </button>
            </div>
          </form>
        </FormProvider>
      )}


      <ResumePdfContent
        isOpen={!!cvSelecionado}
        data={cvSelecionado}
        onClose={() => setCvSelecionado(null)}
      />

      <Modal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, id: null })}
        onConfirm={() => confirmDelete(deleteModal.id)}
        message={t('modals.deleteResumeMessage')}
        confirmText={t('modals.delete')}
        cancelText={t('modals.cancel')}
        variant="danger"
      />
      </div>
    </div>
  );
}
